import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FournisseurService } from '../services/fournisseur.service';
import { HttpServices, QueryParameter } from '../services/http.service';
import { ItemAcountRecherche, ItemAccountType } from '../models';
import { FournisseursModel, AccountResult } from '../models';
import { Right } from '../models/Rights.enum';
import { CdkTableColumns, CdkTableTypeColumn } from '../models/cdk-table.model';
import * as utils from '../utils/utils';
import * as config from '../config/config';
import * as moment from 'moment';
import { ReferenceService } from '../services/reference.service';
import { FilterCriteria } from '../models/filter-criteria.model';
import { LoaderService } from '../services/loader.service';
import { TrackingService } from '../services/tracking.service';
import { error } from 'console';

@Component({
  selector: 'fs-bill-asset',
  styleUrls: ['./bill-asset.sass'],
  templateUrl: './bill-asset.component.html',
  encapsulation: ViewEncapsulation.None
})

export class BillAssetComponent implements OnInit {
  suppliersList: FournisseursModel[];
  activeSupplierId: number;
  rechercheItem: ItemAcountRecherche = new ItemAcountRecherche();
  accountResult: AccountResult = new AccountResult();
  itemsPerPage: number = 10;
  listSizeItemsPerPage: Array<number> = [10, 20, 30, 40, 50];
  currentPage: number = 0;
  askedPage: number = 0;
  totalPages: any[];
  totalEntries: number = 0;

  private columns: Array<CdkTableColumns> =
    [
      {
        title: 'N° pièce',
        responsiveTitle: 'N° pièce:',
        name: 'DocumentNumber',
        sort: '',
        typeColumn: CdkTableTypeColumn.html,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Statut',
        responsiveTitle: 'Statut:',
        name: 'DocumentStatus',
        sort: false,
        typeColumn: CdkTableTypeColumn.html,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Signataire',
        responsiveTitle: 'Signataire:',
        name: 'IdTiersSignataire',
        sort: '',
        typeColumn: CdkTableTypeColumn.html,
        readOnly: true,
        requireReadRight: Right.Cart
      },

      {
        title: 'Facturé',
        responsiveTitle: 'Facturé:',
        name: 'IdTiersFacture',
        sort: '',
        typeColumn: CdkTableTypeColumn.html,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Payeur',
        responsiveTitle: 'Payeur:',
        name: 'IdTiersPayeur',
        sort: '',
        typeColumn: CdkTableTypeColumn.html,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Émis',
        responsiveTitle: 'Émis:',
        name: 'DateEmission',
        sort: '',
        typeColumn: CdkTableTypeColumn.date,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Échéance',
        responsiveTitle: 'Échéance:',
        name: 'DateEcheance',
        sort: 'desc',
        typeColumn: CdkTableTypeColumn.date,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Montant',
        responsiveTitle: 'Montant:',
        name: 'Montant',
        sort: '',
        typeColumn: CdkTableTypeColumn.money,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: 'Solde',
        responsiveTitle: 'Solde:',
        name: 'Solde',
        sort: '',
        typeColumn: CdkTableTypeColumn.money,
        readOnly: true,
        requireReadRight: Right.Cart
      },
      {
        title: '',
        responsiveTitle: 'Télécharger le document ',
        name: 'UrlPdf',
        sort: false,
        typeColumn: CdkTableTypeColumn.imageLink,
        placementTooltip: 'left',
        parentTooltip: false
      }
    ];

  displayedColumns: string[] = this.columns.map(x => x.name);
  hasMultiSuppliers;
  rowsInitialized = false;

  constructor(
    private httpServices: HttpServices,
    private fournisseurService: FournisseurService,
    private referenceService: ReferenceService,
    private loaderService: LoaderService,
    private trackingService: TrackingService
  ) { }

  filterPieces(filterCriteria: FilterCriteria) {
    this.rechercheItem.referenceItem = filterCriteria.numPiece;
    this.rechercheItem.DateDebut = filterCriteria.dateDebut;
    this.rechercheItem.DateFin = filterCriteria.dateFin;
    this.askedPage = 0;
    this.fetchDocuments();
  }

  fournisseurChange(supplierId: number) {
    this.activeSupplierId = supplierId;
    this.fournisseurService.setActiveSupplierId(this.activeSupplierId);
    this.rechercheItem.DateDebut = '';
    this.rechercheItem.DateFin = '';
    this.rechercheItem.referenceItem = '';
    this.fetchDocuments();
  }

  ngOnInit() {
    const colTrie = this.columns.find(col => col.sort === 'asc' || col.sort === 'desc');
    if (colTrie !== undefined) {
      this.calcRechercheTrie(colTrie);
    }

    this.suppliersList = this.fournisseurService.getSuppliersInfosList();
    this.activeSupplierId = this.fournisseurService.getActiveSupplierId();
    this.hasMultiSuppliers = this.fournisseurService.getSuppliersNb() > 1;
    this.fetchDocuments();

    this.trackingService.pushEvent('financial-my_invoices');
    this.trackingService.pushPageViewGA4('financial_invoices');
  }

  private fetchDocuments(): void {
    this.loaderService.increaseLoaderCount();
    let param = this.getObligatoryParam();
    param = param.concat(this.getOptionalParam(this.rechercheItem));

    this.httpServices.get<AccountResult>(this.referenceService.staticReferences.Action[config.ApiKeys.GET_ACCOUNT_LIST], () => { }, param)
      .subscribe(
        response => this.initList(AccountResult.extractData(response))
      );
  }

  calcRechercheTrie(col: CdkTableColumns) {
    this.rechercheItem.trieItemAccount.id = col.name;
    this.rechercheItem.trieItemAsc = col.sort === 'asc';
  }

  onColumnClick($event: CdkTableColumns) {
    this.calcRechercheTrie($event);
    this.fetchDocuments();
  }

  private getObligatoryParam(): QueryParameter[] {
    return this.getPaginationParams([{ 'Key': 'fournisseurId', 'Value': String(this.activeSupplierId) }]);
  }

  private getOptionalParam(itemAcountRecherche: ItemAcountRecherche): QueryParameter[] {
    const params = new Array<any>();

    const DateDebut = moment(itemAcountRecherche.DateDebut, utils.CheckType.getDateFormat(itemAcountRecherche.DateDebut)).toDate();
    const DateFin = moment(itemAcountRecherche.DateFin, utils.CheckType.getDateFormat(itemAcountRecherche.DateFin)).toDate();

    if (itemAcountRecherche.trieItemAccount.id !== '') {
      params.push({
        'Key': 'orderBy',
        'Value': itemAcountRecherche.trieItemAccount.id + (itemAcountRecherche.trieItemAsc ? ' ASC' : ' DESC')
      });
    }

    if (itemAcountRecherche.referenceItem != null && itemAcountRecherche.referenceItem !== '') {
      params.push({ 'Key': 'numPiece', 'Value': itemAcountRecherche.referenceItem });
    }

    if (!isNaN(DateDebut.getTime())) {
      params.push({ 'Key': 'dateMin', 'Value': DateDebut.toDateString() });
    }

    if (!isNaN(DateFin.getTime())) {
      params.push({ 'Key': 'dateMax', 'Value': DateFin.toDateString() });
    }

    params.push({ 'Key': 'typePieceComptables', 'Value': ItemAccountType.Facture });
    params.push({ 'Key': 'typePieceComptables', 'Value': ItemAccountType.Avoir });

    return params;
  }

  private getPaginationParams(params: any[]): QueryParameter[] {
    return params.concat(
      [{ 'Key': 'pageNum', 'Value': String(this.askedPage) },
      { 'Key': 'pageSize', 'Value': String(this.itemsPerPage) }]
    );
  }

  private initList(response: AccountResult) {
    this.rowsInitialized = true;
    this.accountResult = response;
    this.currentPage = response.CurrentPage;
    this.totalPages = new Array(response.TotalPages + 1);
    this.totalEntries = response.TotalEntries;
    this.loaderService.decreaseLoaderCount();
  }

  changePage(page: number) {
    this.askedPage = page;
  }

  onChangeSizePage(sizePage: number): void {
    this.itemsPerPage = sizePage;
    this.askedPage = 0;
    this.fetchDocuments();
  }
}
